import React, { useState } from 'react';
import { Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { siteConfig, faqItems } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Contact form submitted:', formData);
      toast.success('Message sent! We\'ll respond within 24 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section with Logo */}
      <section className="py-16 bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[700px] h-[700px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-2xl mx-auto bg-white/95 backdrop-blur-sm p-8 rounded-lg border-4 border-[#1E3A8A] shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
            <h1 className="text-[#1E3A8A] mb-6">
              Get in <span className="text-[#E31837]">Touch</span>
            </h1>
            <p className="text-[#1E3A8A] text-xl">
              Have questions? We're here to help. Reach out and we'll respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1000px] h-[1000px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-[#1E3A8A] text-2xl font-bold mb-10">Contact Information</h2>

              <div className="space-y-6 mb-16">
                <Card className="bg-white/95 backdrop-blur-sm border-4 border-[#1E3A8A] shadow-[4px_4px_0px_0px_rgba(10,22,40,0.3)]">
                  <CardContent className="p-6 flex items-center gap-5">
                    <div className="w-14 h-14 rounded-lg border-4 border-[#1E3A8A] flex items-center justify-center bg-white">
                      <Phone className="w-6 h-6 text-[#E31837]" />
                    </div>
                    <div>
                      <p className="text-[#1E3A8A] font-medium">Phone / Text</p>
                      <a
                        href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`}
                        className="text-[#1E3A8A] font-bold text-lg hover:text-[#E31837] transition-colors"
                      >
                        {siteConfig.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/95 backdrop-blur-sm border-4 border-[#1E3A8A] shadow-[4px_4px_0px_0px_rgba(10,22,40,0.3)]">
                  <CardContent className="p-6 flex items-center gap-5">
                    <div className="w-14 h-14 rounded-lg border-4 border-[#1E3A8A] flex items-center justify-center bg-white">
                      <Mail className="w-6 h-6 text-[#E31837]" />
                    </div>
                    <div>
                      <p className="text-[#1E3A8A] font-medium">Email</p>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="text-[#1E3A8A] font-bold text-lg hover:text-[#E31837] transition-colors"
                      >
                        {siteConfig.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/95 backdrop-blur-sm border-4 border-[#1E3A8A] shadow-[4px_4px_0px_0px_rgba(10,22,40,0.3)]">
                  <CardContent className="p-6 flex items-center gap-5">
                    <div className="w-14 h-14 rounded-lg border-4 border-[#1E3A8A] flex items-center justify-center bg-white">
                      <Clock className="w-6 h-6 text-[#E31837]" />
                    </div>
                    <div>
                      <p className="text-[#1E3A8A] font-medium">Response Time</p>
                      <p className="text-[#1E3A8A] font-bold text-lg">Within 24 hours</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* FAQ Section with Silhouette */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-8 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
                <h2 className="text-[#1E3A8A] text-2xl font-bold mb-8">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {faqItems.map((faq) => (
                    <AccordionItem
                      key={faq.id}
                      value={`faq-${faq.id}`}
                      className="bg-white border-4 border-[#1E3A8A] rounded-lg px-6 data-[state=open]:border-[#E31837]"
                    >
                      <AccordionTrigger className="text-[#1E3A8A] hover:text-[#E31837] text-left py-5 text-lg font-semibold">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#1E3A8A] pb-5 text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Contact Form with Silhouette */}
            <div>
              <Card className="bg-white/95 backdrop-blur-sm border-4 border-[#1E3A8A] shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
                <CardContent className="p-10">
                  <h2 className="text-[#1E3A8A] text-2xl font-bold mb-8">Send a Message</h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-[#1E3A8A] font-semibold">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-2 bg-white border-4 border-[#1E3A8A] text-[#1E3A8A] focus:border-[#E31837]"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#1E3A8A] font-semibold">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-2 bg-white border-4 border-[#1E3A8A] text-[#1E3A8A] focus:border-[#E31837]"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-[#1E3A8A] font-semibold">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-2 bg-white border-4 border-[#1E3A8A] text-[#1E3A8A] focus:border-[#E31837]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-[#1E3A8A] font-semibold">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="mt-2 bg-white border-4 border-[#1E3A8A] text-[#1E3A8A] focus:border-[#E31837]"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#1E3A8A] font-semibold">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="mt-2 bg-white border-4 border-[#1E3A8A] text-[#1E3A8A] focus:border-[#E31837] min-h-[150px]"
                        placeholder="How can we help you?"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      className="bg-[#1E3A8A] text-white border-4 border-[#1E3A8A] hover:bg-[#E31837] hover:border-[#E31837] w-full text-lg py-6 font-semibold transition-all"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
